/**
 * SettingsPage - Notification settings management
 *
 * Allows users to configure notification settings for the active entity.
 * Settings are stored per-entity with global defaults and entity-specific overrides.
 */

import {
  Box,
  Typography,
  Alert,
  AlertTitle,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { capitalDesignTokens } from '@opengov/capital-mui-theme';
import { PageHeader } from '@opengov/components-page-header';
import type { BreadcrumbItem } from '@opengov/components-page-header';
import { useEntity } from '@/contexts/EntityContext';
import { useLoggerWithContext } from '@/services/logger/loggerContext';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { TestIds } from '@/constants/testIds';
import { useNotificationSettingsQuery } from './settings/hooks/useNotificationSettingsQuery';
import { useNotificationSettingsMutation } from './settings/hooks/useNotificationSettingsMutation';
import {
  resolveSettingsForEntity,
  buildUpdatePayload,
  buildDigestUpdatePayload,
  createDefaultDigestSettings,
  resolveDigestTimezone,
  convertUtcTimeToLocal,
  convertUtcScheduleToLocal,
  DEFAULT_DIGEST_TIME,
  DEFAULT_DELIVERY_DAY,
  DELIVERY_TIME_OPTIONS,
  DELIVERY_DAY_OPTIONS,
  formatDeliveryTime,
  formatDeliveryDay,
  formatTimezoneLabel,
} from './settings/utils';
import { SettingToggle } from './settings/components/SettingToggle';
import { SettingsSection } from './settings/components/SettingsSection';
import type { NotificationType, NotificationChannelKey, DigestFrequency, DeliveryDay } from './types';
import { PATHS } from './routes';

interface SettingsPageProps {
  /**
   * Breadcrumbs to show in the PageHeader. Defaults to the notifications-inbox
   * back-link when omitted (appropriate for the in-app settings sub-route).
   * Pass custom breadcrumbs when rendering this page from a different product
   * area (e.g. the Access Management route registry entry).
   */
  breadcrumbs?: BreadcrumbItem[];
}

const DEFAULT_BREADCRUMBS: BreadcrumbItem[] = [
  { title: 'Tasks and Notifications', path: PATHS.NOTIFICATIONS },
  { title: 'Settings', path: PATHS.SETTINGS },
];

export function SettingsPage({ breadcrumbs = DEFAULT_BREADCRUMBS }: SettingsPageProps) {
  const { activeEntity } = useEntity();
  const logger = useLoggerWithContext();
  const { data, isLoading, error } = useNotificationSettingsQuery();
  const { updateSettings, error: updateError } = useNotificationSettingsMutation();

  // Entity UUID is used for settings resolution (to find entity-specific overrides in the data)
  // Note: The API endpoint uses user UUID in the path (handled by the hooks)
  const entityUuid = activeEntity?.id ?? '';
  const digestTimezone = resolveDigestTimezone(activeEntity?.timezone);

  // Resolve settings for the active entity
  // If user has no settings yet (404), data contains default settings
  // First toggle will create settings on backend via PUT request
  const resolvedSettings = data ? resolveSettingsForEntity(data, entityUuid) : null;

  const handleToggleChange = (
    notificationType: NotificationType,
    settingKey: NotificationChannelKey,
    newValue: boolean
  ) => {
    if (!data) return;

    logger.info(`Updating notification setting: ${notificationType}.${settingKey}`, {
      entity_id: entityUuid,
      notification_type: notificationType,
      setting_key: settingKey,
      new_value: newValue,
    });

    const payload = buildUpdatePayload(data, entityUuid, notificationType, settingKey, newValue);
    updateSettings(payload, {
      onError: (error) => {
        logger.error('Failed to update notification settings', {
          entity_id: entityUuid,
          error: error.message,
        });
      },
      onSuccess: () => {
        logger.info('Successfully updated notification settings', {
          entity_id: entityUuid,
        });
      },
    });
  };

  const handleDeliveryModeChange = (mode: 'realtime' | 'digest') => {
    if (!data) return;
    logger.info('Updating notification delivery mode', { entity_id: entityUuid, mode });
    const payload = buildDigestUpdatePayload(data, entityUuid, {
      email: mode === 'digest',
      timeZone: digestTimezone,
    });
    updateSettings(payload, {
      onError: (error) =>
        logger.error('Failed to update delivery mode', {
          entity_id: entityUuid,
          error: error.message,
        }),
    });
  };

  const handleDigestFrequencyChange = (frequency: DigestFrequency) => {
    if (!data) return;
    logger.info('Updating notification digest frequency', { entity_id: entityUuid, frequency });
    const payload = buildDigestUpdatePayload(data, entityUuid, {
      frequency,
      timeZone: digestTimezone,
    });
    updateSettings(payload, {
      onError: (error) =>
        logger.error('Failed to update digest frequency', {
          entity_id: entityUuid,
          error: error.message,
        }),
    });
  };

  const handleDigestDayChange = (deliveryDay: DeliveryDay) => {
    if (!data) return;
    logger.info('Updating notification digest delivery day', {
      entity_id: entityUuid,
      delivery_day: deliveryDay,
    });
    const payload = buildDigestUpdatePayload(data, entityUuid, {
      deliveryDay,
      timeZone: digestTimezone,
    });
    updateSettings(payload, {
      onError: (error) =>
        logger.error('Failed to update digest delivery day', {
          entity_id: entityUuid,
          error: error.message,
        }),
    });
  };

  const handleDigestTimeChange = (deliveryTime: string) => {
    if (!data) return;

    logger.info('Updating notification digest delivery time', {
      entity_id: entityUuid,
      delivery_time: deliveryTime,
    });

    const payload = buildDigestUpdatePayload(data, entityUuid, {
      deliveryTime,
      timeZone: digestTimezone,
    });
    updateSettings(payload, {
      onError: (error) => {
        logger.error('Failed to update notification digest delivery time', {
          entity_id: entityUuid,
          error: error.message,
        });
      },
    });
  };

  // Loading state - also show loading if data isn't available yet
  // (query might be disabled initially while user info loads)
  if (isLoading || !data || !resolvedSettings) {
    // If there's an error, show error state instead of loading
    if (error) {
      return (
        <Stack
          sx={{
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }}
        >
          <Alert severity="error" sx={{ maxWidth: 600 }}>
            <AlertTitle>Failed to load notification settings</AlertTitle>
            {error?.message || 'Unknown error occurred'}
          </Alert>
        </Stack>
      );
    }

    // Otherwise show loading spinner
    return <LoadingSpinner />;
  }

  // At this point, data and resolvedSettings are guaranteed to exist
  // Error state (shouldn't reach here, but kept for safety)
  if (!resolvedSettings.settings) {
    return (
      <Stack
        sx={{
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <AlertTitle>Invalid notification settings data</AlertTitle>
          Settings data is missing required fields
        </Alert>
      </Stack>
    );
  }

  const informationalEmailEnabled = resolvedSettings.settings.informational.email;
  const informationalDigests =
    resolvedSettings.settings.informational.digests ?? createDefaultDigestSettings();
  const digestEnabled = informationalEmailEnabled && informationalDigests.email;
  const deliveryMode: 'realtime' | 'digest' = digestEnabled ? 'digest' : 'realtime';
  const digestFrequency: DigestFrequency = informationalDigests.schedule.frequency ?? 'daily';
  const localSchedule =
    digestEnabled && digestFrequency === 'weekly' && informationalDigests.schedule.deliveryDay
      ? convertUtcScheduleToLocal(
          informationalDigests.schedule.deliveryDay,
          informationalDigests.schedule.deliveryTime,
          digestTimezone
        )
      : {
          deliveryDay: DEFAULT_DELIVERY_DAY,
          deliveryTime: digestEnabled
            ? convertUtcTimeToLocal(informationalDigests.schedule.deliveryTime, digestTimezone)
            : DEFAULT_DIGEST_TIME,
        };
  const localDeliveryDay: DeliveryDay = localSchedule.deliveryDay;
  const localDeliveryTime = localSchedule.deliveryTime;
  const deliveryTimeOptions = (
    DELIVERY_TIME_OPTIONS.includes(localDeliveryTime)
      ? DELIVERY_TIME_OPTIONS
      : [...DELIVERY_TIME_OPTIONS, localDeliveryTime].sort()
  ).map((time) => ({ value: time, label: formatDeliveryTime(time) }));
  const timezoneLabel = formatTimezoneLabel(digestTimezone);
  const digestScheduleText =
    digestFrequency === 'weekly'
      ? `Delivered weekly on ${formatDeliveryDay(localDeliveryDay)} in ${timezoneLabel}`
      : `Delivered daily in ${timezoneLabel}`;

  return (
    <Stack
      sx={{
        height: '100%',
        overflowY: 'auto',
        backgroundColor: capitalDesignTokens.semanticColors.background.primary,
      }}
    >
      <PageHeader
        title="Notification Settings"
        maxContentWidth="100%"
        sx={{
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
        titleSize="small"
        description="Choose how and when you're notified about tasks, updates, and deadlines."
        breadcrumbs={breadcrumbs}
      />
      <Box sx={{ maxWidth: 800, mx: 'auto', pt: 4 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Manage how you receive notifications for{' '}
          <Typography component="span" sx={{ fontWeight: 600 }}>
            {activeEntity?.name || 'this entity'}
          </Typography>
          .
          {resolvedSettings.isOverride && (
            <Typography component="span" sx={{ fontStyle: 'italic', ml: 1, color: 'info.main' }}>
              (Custom settings for this entity)
            </Typography>
          )}
        </Typography>

        {updateError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Failed to update settings</AlertTitle>
            {updateError.message}
          </Alert>
        )}

        <SettingsSection
          title="Actionable Notifications"
          description="Notifications that require your attention or action."
        >
          <Alert severity="info" sx={{ m: 2 }}>
            <AlertTitle>In-app notifications are always enabled</AlertTitle>
            Actionable notifications will always appear in your notification center to ensure you
            don&apos;t miss important updates requiring your attention. You can still choose whether
            to also receive them via email.
          </Alert>
          <SettingToggle
            label="In-App Notifications"
            description="Receive actionable notifications in the notification center"
            checked={true}
            onChange={(checked) => handleToggleChange('actionable', 'inApp', checked)}
            disabled={true}
          />
          <SettingToggle
            label="Email Notifications"
            description="Receive actionable notifications via email"
            checked={resolvedSettings.settings.actionable.email}
            onChange={(checked) => handleToggleChange('actionable', 'email', checked)}
          />
        </SettingsSection>

        <SettingsSection
          title="Informational Notifications"
          description="General updates and information about your account and activities."
        >
          <SettingToggle
            label="In-App Notifications"
            description="Receive informational notifications in the notification center"
            checked={resolvedSettings.settings.informational.inApp}
            onChange={(checked) => handleToggleChange('informational', 'inApp', checked)}
          />
          <SettingToggle
            label="Email Notifications"
            description="Receive informational notifications via email"
            checked={informationalEmailEnabled}
            onChange={(checked) => handleToggleChange('informational', 'email', checked)}
          >
            {informationalEmailEnabled && (
              <Stack spacing={2} sx={{ mt: 2, maxWidth: 280 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="delivery-mode-label">Email delivery</InputLabel>
                  <Select
                    labelId="delivery-mode-label"
                    label="Email delivery"
                    value={deliveryMode}
                    onChange={(e) =>
                      handleDeliveryModeChange(e.target.value as 'realtime' | 'digest')
                    }
                    data-testid={TestIds.NOTIFICATION_SETTINGS_DELIVERY_MODE}
                  >
                    <MenuItem value="realtime">Real-time</MenuItem>
                    <MenuItem value="digest">Digest</MenuItem>
                  </Select>
                </FormControl>

                {deliveryMode === 'digest' && (
                  <Stack spacing={1.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="digest-frequency-label">Frequency</InputLabel>
                      <Select
                        labelId="digest-frequency-label"
                        label="Frequency"
                        value={digestFrequency}
                        onChange={(e) =>
                          handleDigestFrequencyChange(e.target.value as DigestFrequency)
                        }
                        data-testid={TestIds.NOTIFICATION_SETTINGS_DIGEST_FREQUENCY}
                      >
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                      </Select>
                    </FormControl>

                    {digestFrequency === 'weekly' && (
                      <FormControl fullWidth size="small">
                        <InputLabel id="digest-day-label">Day</InputLabel>
                        <Select
                          labelId="digest-day-label"
                          label="Day"
                          value={localDeliveryDay}
                          onChange={(e) =>
                            handleDigestDayChange(e.target.value as DeliveryDay)
                          }
                          data-testid={TestIds.NOTIFICATION_SETTINGS_DIGEST_DAY}
                        >
                          {DELIVERY_DAY_OPTIONS.map(({ value, label }) => (
                            <MenuItem key={value} value={value}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    <FormControl fullWidth size="small">
                      <InputLabel id="digest-delivery-time-label">Delivery time</InputLabel>
                      <Select
                        labelId="digest-delivery-time-label"
                        label="Delivery time"
                        value={localDeliveryTime}
                        onChange={(e) => handleDigestTimeChange(e.target.value)}
                        data-testid={TestIds.NOTIFICATION_SETTINGS_DIGEST_TIME}
                      >
                        {deliveryTimeOptions.map(({ value, label }) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Typography variant="body2" color="text.secondary">
                      {digestScheduleText}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}
          </SettingToggle>
        </SettingsSection>

        <SettingsSection title="Mentions" description="Notifications when someone mentions you.">
          <SettingToggle
            label="In-App Notifications"
            description="Receive mention notifications in the notification center"
            checked={resolvedSettings.settings.mentions.inApp}
            onChange={(checked) => handleToggleChange('mentions', 'inApp', checked)}
          />
          <SettingToggle
            label="Email Notifications"
            description="Receive mention notifications via email"
            checked={resolvedSettings.settings.mentions.email}
            onChange={(checked) => handleToggleChange('mentions', 'email', checked)}
          />
        </SettingsSection>
      </Box>
    </Stack>
  );
}
